import React, { useEffect, useState, useId } from "react";
import { getTextileFilters } from "../../services/productService";
import "../../css/Filter.css";

// Friendly section names (the API keys are camelCase, e.g. "productType")
const LABELS = {
  sizes: "Size",
  colors: "Colour",
  productType: "Product Type",
  sleeveOrStyle: "Sleeve / Style",
  material: "Fabric",
  brand: "Brand",
  subCategory: "Category",
  category: "Department"
};

const ORDER = ["sizes", "colors", "productType", "material", "sleeveOrStyle", "brand", "subCategory", "category"];

const humanize = (key) =>
  LABELS[key] || key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());

const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL", "5XL", "FREE", "FREE SIZE"];
const sortSizes = (list = []) =>
  [...list].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    const ia = SIZE_ORDER.indexOf(String(a).toUpperCase());
    const ib = SIZE_ORDER.indexOf(String(b).toUpperCase());
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return String(a).localeCompare(String(b));
  });

const Filter = ({
  onFilterChange,
  onFilterPreview,
  totalProducts = 0,
  onClose,
  maxPrice = 10000,
  priceRange,
  onPriceChange,
  scope = {}, // { category, subCategory } from the page URL
  counts = {} // { key: { value: number } } from the listing
}) => {
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState({});
  const [openSections, setOpenSections] = useState({});

  // Options only for what's on this page (e.g. Kurti sizes on the Kurti page)
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const params = {};
        if (scope.subCategory) params.subCategory = scope.subCategory;
        const data = await getTextileFilters(params);
        if (!isMounted) return;

        const { priceRange: _ignore, ...rest } = data || {};
        if (scope.subCategory) delete rest.subCategory;
        if (scope.category || scope.subCategory) delete rest.category;
        if (rest.sizes) rest.sizes = sortSizes(rest.sizes);

        const ordered = {};
        [...ORDER, ...Object.keys(rest)].forEach((k) => {
          if (rest[k]?.length && !ordered[k]) ordered[k] = rest[k];
        });

        setFilters(ordered);
        setSelected({});
        const open = {};
        Object.keys(ordered).forEach((k, i) => {
          open[k] = i < 3; // Keep first 3 sections expanded by default
        });
        setOpenSections(open);
      } catch (error) {
        console.error("Filter load error:", error);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [scope.category, scope.subCategory]);

  const maxLimit = maxPrice > 0 ? maxPrice : 10000;
  const currentMin = Math.max(0, priceRange?.min ?? 0);
  const currentMax = Math.min(maxLimit, priceRange?.max ?? maxLimit);

  const minPercent = Math.min(100, Math.max(0, (currentMin / maxLimit) * 100));
  const maxPercent = Math.min(100, Math.max(0, (currentMax / maxLimit) * 100));

  const handleMinSlider = (e) => {
    const val = Math.min(Number(e.target.value), Math.max(0, currentMax - 10));
    onPriceChange?.({ min: Math.max(0, val), max: currentMax });
  };

  const handleMaxSlider = (e) => {
    const val = Math.max(Number(e.target.value), Math.min(maxLimit, currentMin + 10));
    onPriceChange?.({ min: currentMin, max: Math.min(maxLimit, val) });
  };

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const apply = (next) => {
    setSelected(next);
    onFilterPreview?.(next);
    onFilterChange?.(next);
  };

  const handleChange = (type, value) => {
    const updated = { ...selected };
    const current = updated[type] || [];
    updated[type] = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    if (!updated[type].length) delete updated[type];
    apply(updated);
  };

  const clearPrice = () => {
    onPriceChange?.({ min: 0, max: maxLimit });
  };

  const clearAll = () => {
    apply({});
    clearPrice();
  };

  const selectedCount = Object.values(selected).flat().length;
  const priceActive = currentMin > 0 || currentMax < maxLimit;

  return (
    <div className="brf-sidebar">
      {/* ---------- HEADER (Fixed on mobile drawer) ---------- */}
      <div className="brf-header-row">
        <div className="brf-header-title-wrap">
          <h3 className="brf-title">Filters</h3>
          {(selectedCount > 0 || priceActive) && (
            <span className="brf-count-badge">
              {selectedCount + (priceActive ? 1 : 0)}
            </span>
          )}
        </div>

        {(selectedCount > 0 || priceActive) && (
          <button type="button" className="brf-clear-link" onClick={clearAll}>
            Clear all
          </button>
        )}

        <button
          type="button"
          className="brf-close-btn"
          onClick={onClose}
          aria-label="Close filters"
        >
          ✕
        </button>
      </div>

      <div className="brf-body">
        {/* ---------- ACTIVE CHIPS ---------- */}
        {(selectedCount > 0 || priceActive) && (
          <div className="brf-chips">
            {Object.entries(selected).map(([type, values]) =>
              values.map((value) => (
                <button
                  key={`${type}-${value}`}
                  type="button"
                  className="brf-chip"
                  onClick={() => handleChange(type, value)}
                  title={`Remove ${value}`}
                >
                  <span>{type === "sizes" ? `Size ${value}` : value}</span>
                  <span className="brf-chip-x" aria-hidden="true">✕</span>
                </button>
              ))
            )}
            {priceActive && (
              <button
                type="button"
                className="brf-chip"
                onClick={clearPrice}
                title="Reset price filter"
              >
                <span>₹{currentMin} – ₹{currentMax}</span>
                <span className="brf-chip-x" aria-hidden="true">✕</span>
              </button>
            )}
          </div>
        )}

        {/* ---------- DUAL RANGE SLIDER PRICE FILTER ---------- */}
        <div className="brf-price-section">
          <div className="brf-price-header">
            <span className="brf-price-label">Price Range</span>
            {priceActive && (
              <button
                type="button"
                className="brf-price-reset"
                onClick={clearPrice}
              >
                Reset
              </button>
            )}
          </div>

          <div className="brf-price-values">
            <div className="brf-price-input-box">
              <span className="brf-currency">₹</span>
              <input
                type="number"
                min={0}
                max={Math.max(0, currentMax - 10)}
                value={currentMin}
                onChange={(e) => {
                  const val = Math.max(0, Math.min(Number(e.target.value) || 0, currentMax - 10));
                  onPriceChange?.({ min: val, max: currentMax });
                }}
                className="brf-price-num-input"
                aria-label="Minimum price"
              />
            </div>
            <span className="brf-price-dash">to</span>
            <div className="brf-price-input-box">
              <span className="brf-currency">₹</span>
              <input
                type="number"
                min={currentMin + 10}
                max={maxLimit}
                value={currentMax}
                onChange={(e) => {
                  const val = Math.min(maxLimit, Math.max(Number(e.target.value) || maxLimit, currentMin + 10));
                  onPriceChange?.({ min: currentMin, max: val });
                }}
                className="brf-price-num-input"
                aria-label="Maximum price"
              />
            </div>
          </div>

          <div className="brf-slider-wrap">
            <div className="brf-slider-track" />
            <div
              className="brf-slider-fill"
              style={{
                left: `${minPercent}%`,
                width: `${Math.max(0, maxPercent - minPercent)}%`
              }}
            />
            <input
              type="range"
              min={0}
              max={maxLimit}
              step={10}
              value={currentMin}
              onChange={handleMinSlider}
              className="brf-range brf-range-min"
              style={{ zIndex: currentMin > maxLimit * 0.75 ? 4 : 2 }}
              aria-label="Minimum price slider"
            />
            <input
              type="range"
              min={0}
              max={maxLimit}
              step={10}
              value={currentMax}
              onChange={handleMaxSlider}
              className="brf-range brf-range-max"
              style={{ zIndex: 3 }}
              aria-label="Maximum price slider"
            />
          </div>
        </div>

        {/* ---------- ACCORDION SECTIONS ---------- */}
        {Object.entries(filters).map(([key, values]) => {
          const isOpen = !!openSections[key];
          const hasSelected = selected[key]?.length > 0;

          return (
            <div className="brf-section" key={key}>
              <button
                type="button"
                className="brf-section-head"
                onClick={() => toggleSection(key)}
                aria-expanded={isOpen}
              >
                <span className="brf-section-title">
                  {humanize(key)}
                  {hasSelected && (
                    <span className="brf-section-count">
                      {" "}({selected[key].length})
                    </span>
                  )}
                </span>
                <span className="brf-toggle-icon" aria-hidden="true">
                  {isOpen ? "−" : "+"}
                </span>
              </button>

              {isOpen && (
                <div
                  className={`brf-options ${
                    key === "sizes" ? "brf-options-sizes" : ""
                  }`}
                >
                  {values.map((item) => {
                    const isOn = selected[key]?.includes(item) || false;
                    const n = counts[key] ? counts[key][item] || 0 : null;
                    const empty = n === 0 && !isOn;

                    return (
                      <label
                        className={`brf-option ${isOn ? "is-selected" : ""} ${
                          empty ? "is-empty" : ""
                        }`}
                        key={item}
                      >
                        <input
                          type="checkbox"
                          checked={isOn}
                          disabled={empty}
                          onChange={() => handleChange(key, item)}
                        />
                        <span>
                          {item}
                          {n !== null && key !== "sizes" && (
                            <em className="brf-count"> ({n})</em>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ---------- MOBILE ACTION BAR ---------- */}
      <div className="brf-actions">
        <button
          type="button"
          className="brf-clear-all-btn"
          onClick={clearAll}
          disabled={selectedCount === 0 && !priceActive}
        >
          Clear All
        </button>
        <button
          type="button"
          className="brf-apply-btn"
          onClick={onClose}
        >
          Show {totalProducts} {totalProducts === 1 ? "Product" : "Products"}
        </button>
      </div>
    </div>
  );
};

export default Filter;
